/**
 * Vendor-approved sale windows (flag `vendor_sale_windows`, MOT-157): the dates
 * inside which a brand's dealers may advertise below MAP. The vendor defines
 * them at `/vendor/sale-windows`; a store reads its own at `/store/sale-windows`.
 */
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

export type SaleWindowStatus = 'active' | 'expired' | 'cancelled';

/** A catalog product a window names (MOT-370): identity only, never a price. */
export interface SaleWindowProduct {
  id: string;
  name: string;
  model: string | null;
  sku: string | null;
}

export interface VendorSaleWindow {
  id: string;
  vendorOrganizationId: string;
  brandId: string;
  brandName: string;
  /** One dealer's window, or null for every verified dealer of the brand. */
  organizationId: string | null;
  organizationName: string | null;
  /** The products the window covers, or null for every product of the brand. */
  productIds: string[] | null;
  products: SaleWindowProduct[] | null;
  name: string;
  startAt: string;
  endAt: string;
  floorPrice: number | null;
  maxPercentBelowMap: number | null;
  floorPrivate: boolean;
  note: string | null;
  status: SaleWindowStatus;
  createdAt: string;
  updatedAt: string;
}

export interface StoreSaleWindow {
  id: string;
  brandId: string;
  brandName: string;
  dealerSpecific: boolean;
  /** False until the store is verified for the brand: the window is visible, pricing under it is not allowed yet. */
  verified: boolean;
  /** The products the window covers, or null for every product of the brand. */
  products: SaleWindowProduct[] | null;
  name: string;
  startAt: string;
  endAt: string;
  floorPrice: number | null;
  maxPercentBelowMap: number | null;
  /** The vendor set a floor but kept the number private. */
  floorHidden: boolean;
  note: string | null;
  status: 'upcoming' | 'active';
}

export interface SaleWindowInput {
  name: string;
  organizationId?: string | null;
  /** Products of the brand the window covers; omitted or null for the whole brand. Fixed at creation. */
  productIds?: string[] | null;
  startAt: string;
  endAt: string;
  floorPrice?: number | null;
  maxPercentBelowMap?: number | null;
  floorPrivate?: boolean;
  note?: string | null;
}

export interface VendorDealerOption {
  organizationId: string;
  name: string;
}

async function call<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const payload = await request(path, init);
  return payload?.data as T;
}

async function request(path: string, init?: { method?: string; body?: unknown }): Promise<any> {
  const res = await makeApiCall(path, {
    method: init?.method || 'GET',
    headers: await getAuthHeaders(),
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(payload?.error?.message || payload?.message || 'Request failed') as SaleWindowsApiError;
    error.status = res.status;
    throw error;
  }
  return payload;
}

/** The picker's catalog; `truncated` means the server cap was hit and the list is partial. */
export interface SaleWindowProductList {
  products: SaleWindowProduct[];
  truncated: boolean;
}

/** A failed call carries the HTTP status, so a 403 (no brand of its own) can be told from a failure. */
export type SaleWindowsApiError = Error & { status: number };

/** The per-brand rule (MOT-388): the brand allows sale pricing only inside vendor-approved windows. */
export interface BrandSalePolicy {
  brandId: string;
  brandName: string;
  saleWindowsRequired: boolean;
}

export const VENDOR_SALE_POLICY_QUERY_KEY = ['vendor-sale-window-policy'] as const;
export const getVendorSalePolicy = (): Promise<BrandSalePolicy> => call('/vendor/sale-windows/policy');
export const setVendorSalePolicy = (saleWindowsRequired: boolean): Promise<BrandSalePolicy> =>
  call('/vendor/sale-windows/policy', { method: 'PUT', body: { saleWindowsRequired } });

export const STORE_SALE_POLICIES_QUERY_KEY = ['store-sale-window-policies'] as const;
export const listStoreSalePolicies = (): Promise<BrandSalePolicy[]> => call('/store/sale-windows/policies');

export const VENDOR_SALE_WINDOWS_QUERY_KEY = ['vendor-sale-windows'] as const;
export const listVendorSaleWindows = (): Promise<VendorSaleWindow[]> => call('/vendor/sale-windows');
export const listVendorDealers = (): Promise<VendorDealerOption[]> => call('/vendor/sale-windows/stores');
/** The brand's active catalog products for the window's product picker (capped server-side). */
export const listVendorSaleWindowProducts = async (): Promise<SaleWindowProductList> => {
  const payload = await request('/vendor/sale-windows/products');
  return { products: payload?.data || [], truncated: payload?.truncated === true };
};
export const createVendorSaleWindow = (input: SaleWindowInput): Promise<VendorSaleWindow> =>
  call('/vendor/sale-windows', { method: 'POST', body: input });
/**
 * Edits an active window; `productIds` omitted keeps its list, null makes it
 * whole-brand (MOT-415). `overridesEnded` counts the store prices a narrowed
 * list ended (their listings are repriced to MAP).
 */
export const updateVendorSaleWindow = async (
  id: string,
  input: Omit<SaleWindowInput, 'organizationId'>,
): Promise<{ window: VendorSaleWindow; overridesEnded: number }> => {
  const payload = await request(`/vendor/sale-windows/${id}`, { method: 'PUT', body: input });
  return { window: payload?.data as VendorSaleWindow, overridesEnded: Number(payload?.overridesEnded) || 0 };
};
export const cancelVendorSaleWindow = (id: string): Promise<VendorSaleWindow> =>
  call(`/vendor/sale-windows/${id}`, { method: 'DELETE' });

export const STORE_SALE_WINDOWS_QUERY_KEY = ['store-sale-windows'] as const;
export const listStoreSaleWindows = (): Promise<StoreSaleWindow[]> => call('/store/sale-windows');
